import React from "react";
import { graphql } from "gatsby";
import { usePaginatedList } from "../utils/use-paginated-list";
import parse from "html-react-parser";
import PropTypes from "prop-types";
import Button from "./button";
import Card from "./card";
import CardContainer from "./card-container";
import Columns from "./columns";
import Column from "./column";
import ContactForm from "./contact-form";
import Container from "./container";
import GutenbergBlocks from "./gutenberg-blocks";
import Hero from "./hero";
import Image from "./image";
import InstagramFeed from "./instagram-feed";
import Location from "./location";
import MasonryImageGrid from "./masonryimagegrid";
import SectionTitle from "./sectiontitle";
import VideoCard from "./videocard";
import Quotes from "./quotes";
import BlogCard from "./blogcard";
import Metric from "./metric";
import InfoCard from "./infocard";
import FaqWrapper from "./faqwrapper";
import Accordion from "./accordion";
import SuperHero from "./superhero";
import EmployeeCard from "./employee-card";
import EmployeeCardContainer from "./employee-card-container";
import InfoDivider from "./info-divider";
import Cart from "./cart";
import CTACard from "./ctacard";
import { Cover } from "./cover";
import Heading from "./heading";
import Paragraph from "./paragraph";
import Checkout from "./checkout";
import FeaturedProduct from "./featured-product";
import FileDownload from "./filedownload";
import SuperheroSlide from "./superhero-slide";
import Breadcrumbs from "./breadcrumbs";
import MediaText from "./media-text";
import BlogcardContainer from "./blogcard-container";
import Section from "./section";
import MultipleProductsBlock from "./multiple-products-block";
import ProductCategoryBlock from "./product-category-block";
import GalleryContainer from "./gallery-container";
import Hidden from "./hidden";
import SingleQuote from "./single-quote";
import Spacer from "./spacer";
import ReadMore from "./read-more";
import List from "./list";
import Code from "./code";
import LightBoxGallery from "./lightbox-gallery";

export default function GutenbergBlock({ block, seo = "", ...props }) {
  switch (block.name) {
    case "core/button":
      return (
        <Button
          variant={
            block.attributes.className === "is-style-outline"
              ? "outlined"
              : "filled"
          }
          label={block.attributes.text}
          target={block.attributes.linkTarget}
          rel={block.attributes.rel}
          url={block.attributes.url?.replace(process.env.GATSBY_SITE_URL, "")}
          innerClassName={block.attributes.className}
          backgroundColor={block.attributes?.backgroundColorHex}
          textColor={block.attributes?.textColorHex}
          borderRadius={block.attributes?.borderRadius}
          padding={block.attributes?.padding}
          letterSpacing={block.attributes?.customLetterSpacing}
          rowSpacing={block.attributes?.rowSpacing}
        />
      );

    case "woocommerce/cart":
      return <Cart name={block.name} />;

    case "core/buttons":
      return (
        <div
          style={{
            justifyContent: `${block.attributes.contentJustification}`,
          }}
          className="button-container"
        >
          <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />
        </div>
      );

    case "core/block":
      return (
        <GutenbergBlocks blocks={block.attributes?.reusableBlock?.blocks} />
      );

    case "core/code":
      return <Code code={block.attributes?.code} />;

    case "core/columns":
      return (
        <Columns
          className={block.attributes.className}
          noGap={block.attributes?.blockGap}
          padding={block.attributes?.padding}
          backgroundColor={block?.attributes?.backgroundColorHex}
          textColor={block?.attributes?.textColorHex}
        >
          <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />
        </Columns>
      );

    case "core/column":
      return (
        <Column
          className={block.attributes.className}
          width={block.attributes.width}
          verticalAlignment={block.attributes.verticalAlignment || "top"}
          backgroundColor={block.attributes.backgroundColorHex}
          textColor={block.attributes.textColorHex}
          padding={block.attributes?.padding}
          height={block.attributes?.columnHeight}
        >
          <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />
        </Column>
      );
    case "core/cover":
      return (
        <Cover
          minHeight={block.attributes?.minHeight}
          minHeightUnit={block.attributes?.minHeightUnit}
          align={block.attributes?.align}
          media={block.attributes?.media}
          dimRatio={block.attributes?.dimRatio}
          duotoneColorValues={
            block.attributes?.duotoneColorValues
              ? JSON.parse(block.attributes.duotoneColorValues)
              : undefined
          }
          overlayColor={block.attributes?.overlayColor}
          contentPosition={block.attributes?.contentPosition}
          hasParallax={block.attributes?.hasParallax}
          padding={block.attributes?.padding}
          {...props}
        >
          <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />
        </Cover>
      );
    case "core/group":
      return <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />;

    case "core/image": {
      const maxWidth = ((size) => {
        switch (size) {
          case "full":
            return "xl";
          case "thumbnail":
            return "xs";
          case "medium":
            return "sm";
          case "large":
          default:
            return "md";
        }
      })(block.attributes.sizeSlug);
      return (
        <Container
          maxWidth={maxWidth}
          disableMargin={true}
          disableGutters={true}
          className={block.attributes.className}
        >
          {block?.attributes?.href !== "" ? (
            <a
              href={block.attributes.href}
              target={block?.attributes?.rel === "noopener" ? "_blank" : ""}
              rel={"noreferrer"}
            >
              <Image
                image={block.attributes.image}
                className={`img-width ${block.attributes.className}`}
                alt={block.attributes.alt}
                width={block.attributes.imageWidth}
                height={block.attributes.imageHeight}
              />
            </a>
          ) : (
            <Image
              image={block.attributes.image}
              className={`img-width ${block.attributes.className}`}
              alt={block.attributes.alt}
              width={block.attributes.imageWidth}
              height={block.attributes.imageHeight}
            />
          )}
          {block.attributes.caption && (
            <p
              className="center"
              dangerouslySetInnerHTML={{ __html: block.attributes.caption }}
            />
          )}
        </Container>
      );
    }

    case "core/gallery": {
      const photos = block.innerBlocks.reduce((photos, block) => {
        if (block.name === "core/image") {
          photos.push({ image: block.attributes.image });
        }
        return photos;
      }, []);
      return (
        <GalleryContainer
          photos={photos}
          columns={block.attributes?.gridCol}
          fixedHeight={block.attributes.fixedHeight}
          imageCrop={block.attributes.imageCrop}
          className={block.attributes.className}
        />
      );
    }

    case "core/media-text":
      return (
        <MediaText
          align={block.attributes?.mevAlign}
          verticalAlignment={block.attributes?.verticalAlignment}
          backgroundColor={block.attributes?.backgroundColor}
          textColor={block.attributes?.textColor}
          mediaWidth={block.attributes.mediaWidth}
          mediaPosition={block.attributes.mediaPosition}
          imageFill={block.attributes?.imageFill}
          className={block.attributes?.className}
          media={block.attributes?.media}
          letterSpacing={block.attributes?.customLetterSpacing}
          rowSpacing={block.attributes?.rowSpacing}
          linkColor={block.attributes?.linkColor}
        >
          <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />
        </MediaText>
      );

    case "core/latest-posts": {
      const [postList, LoadMorePostsButton] = usePaginatedList(
        block.attributes.posts,
        9
      );
      return (
        <>
          <BlogcardContainer
            postLayout={block.attributes.postLayout}
            columns={block.attributes.columns}
          >
            {postList.map((post, index) => (
              <BlogCard
                key={index}
                title={post.title}
                date={post.date}
                excerpt={post.excerpt}
                link={post.uri}
                image={post?.featuredImage?.node}
                author={post.author.node.name}
                displayAuthor={block.attributes.displayAuthor}
                displayFeaturedImage={block.attributes.displayFeaturedImage}
                displayPostContent={block.attributes.displayPostContent}
                displayPostDate={block.attributes.displayPostDate}
                columns={block.attributes.columns}
                layout={block.attributes.postLayout}
                excerptLength={block.attributes.excerptLength}
                letterSpacing={block.attributes?.customLetterSpacing}
                rowSpacing={block.attributes?.rowSpacing}
                linkColor={block.attributes?.linkColor}
              />
            ))}
          </BlogcardContainer>
          <LoadMorePostsButton>Last in flere</LoadMorePostsButton>
        </>
      );
    }

    case "core/paragraph":
      return (
        <Paragraph
          backgroundColor={block.attributes?.backgroundColorHex}
          color={block.attributes?.textColorHex}
          content={block?.attributes?.content}
          fontSize={block?.attributes?.parsedFontSize}
          className={block?.attributes?.className}
          align={block?.attributes?.align}
          anchor={block?.attributes?.anchor}
          direction={block?.attributes?.direction}
          textTransform={block?.attributes?.textTransform}
          fontStyle={block?.attributes?.fontStyle}
          letterSpacing={block?.attributes?.letterSpacing}
          fontWeight={block?.attributes?.fontWeight}
          fontSizeMobile={block?.attributes?.fontSizeMobile}
          rowSpacing={block?.attributes?.rowSpacing}
          linkColor={block.attributes?.linkColor}
          inlineImageWidth={block.attributes?.inlineImageWidth}
        />
      );
    case "core/heading":
      return (
        <Heading
          level={block.attributes?.level}
          anchor={block.attributes?.anchor}
          className={block.attributes?.className}
          margin={block.attributes?.margin}
          fontSize={block.attributes?.parsedFontSize}
          color={block.attributes?.textColorHex}
          backgroundColor={block.attributes?.backgroundColorHex}
          textAlign={block.attributes?.textAlign}
          content={block?.attributes?.content}
          textTransform={block?.attributes?.textTransform}
          fontStyle={block?.attributes?.fontStyle}
          letterSpacing={block?.attributes?.letterSpacing}
          fontWeight={block?.attributes?.fontWeight}
          fontSizeMobile={block?.attributes?.fontSizeMobile}
          rowSpacing={block?.attributes?.rowSpacing}
          linkColor={block.attributes?.linkColor}
        />
      );

    case "core/spacer":
      return (
        <Spacer
          className={block.attributes?.className}
          height={block.attributes?.spacerHeight}
          mobileHeight={block.attributes?.spacerHeightMobile}
        />
      );
    case "core/file":
      return (
        <FileDownload
          fileName={block.attributes?.fileName}
          url={block.attributes?.href}
          showDownloadButton={block.attributes?.showDownloadButton}
          downloadButtonText={block.attributes?.downloadButtonText}
        ></FileDownload>
      );

    case "core/list":
      return (
        <List
          letterSpacing={block?.attributes?.letterSpacing}
          rowSpacing={block?.attributes?.rowSpacing}
          values={block?.attributes?.values}
          linkColor={block.attributes?.linkColor}
        />
      );

    case "mev/card":
      return block.attributes?.variation === "cta" ? (
        <CTACard
          image={block.attributes?.cardImage}
          label={block.attributes?.urlLabel}
          linkUrl={block.attributes?.page?.uri || block.attributes?.url}
          opacity={block.attributes?.opacity}
        />
      ) : (
        <Card
          title={block.attributes?.title}
          image={block.attributes?.cardImage}
          description={block.attributes?.description}
          label={block.attributes?.urlLabel}
          linkUrl={block.attributes?.page?.uri || block.attributes?.url}
          twoCol={block.attributes?.twoCol}
          backgroundColor={block.attributes?.backgroundColorHex}
          textColor={block.attributes?.textColorHex}
          buttonTextColor={block.attributes?.buttonTextColorHex}
          letterSpacing={block.attributes?.customLetterSpacing}
          rowSpacing={block.attributes?.rowSpacing}
          linkColor={block.attributes?.linkColor}
        />
      );

    case "mev/card-container":
      return (
        <CardContainer
          autoplay={block.attributes.autoplay}
          postLayout={block.attributes.postLayout}
          columns={block.attributes.nrOfColumns}
          className={block.attributes.className}
        >
          {block.innerBlocks.map((block, i) => (
            <GutenbergBlock key={i} block={block} />
          ))}
        </CardContainer>
      );

    case "mev/container":
      return (
        <Container
          disableGutters={block.attributes.disableGutters}
          disableMargin={block.attributes.disableMargins}
          maxWidth={block.attributes.maxWidth}
          variant={block.attributes.variant}
          alignContent={block.attributes.alignContent}
          disableAnimation={block.attributes.disableAnimation}
          backgroundColor={block.attributes.backgroundColorHex}
          textColor={block.attributes.textColorHex}
          className={block.attributes?.className}
          borderRadius={block.attributes?.borderRadius}
        >
          <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />
        </Container>
      );

    case "mev/employee-card":
      return (
        <EmployeeCard
          title={block.attributes.title}
          image={block.attributes.image}
          name={block.attributes.name}
          city={block.attributes.city}
          email={block.attributes.email}
          phone={block.attributes.phone}
          backgroundColorHex={block.attributes?.backgroundColorHex}
          textColorHex={block.attributes?.textColorHex}
          letterSpacing={block.attributes?.customLetterSpacing}
          rowSpacing={block.attributes?.rowSpacing}
          linkColor={block.attributes?.linkColor}
          className={block.attributes?.className}
        />
      );

    case "mev/employee-card-container":
      return (
        <EmployeeCardContainer
          postLayout={block.attributes.postLayout}
          columns={block.attributes.nrOfColumns}
          className={block.attributes.className}
        >
          {block.innerBlocks.map((block, i) => (
            <GutenbergBlock key={i} block={block} />
          ))}
        </EmployeeCardContainer>
      );

    case "mev/faq-container":
      return (
        <FaqWrapper
          backgroundColor={block?.attributes?.backgroundColorHex}
          textColor={block?.attributes?.textColorHex}
        >
          {block.innerBlocks.map((block, i) => (
            <GutenbergBlock key={i} block={block} seo={seo} />
          ))}
        </FaqWrapper>
      );
    case "mev/faq":
      return (
        <Accordion
          answer={block.attributes.answer}
          question={block.attributes.question}
          backgroundColor={block?.attributes?.backgroundColorHex}
          textColor={block?.attributes?.textColorHex}
          letterSpacing={block.attributes?.customLetterSpacing}
          rowSpacing={block.attributes?.rowSpacing}
          linkColor={block.attributes?.linkColor}
        />
      );

    case "mev/hidden":
      return (
        <Hidden
          $showOnDesktop={block.attributes.showOnDesktop}
          $showOnTablet={block.attributes.showOnTablet}
          $showOnPhone={block.attributes.showOnPhone}
        >
          <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />
        </Hidden>
      );

    case "mev/info-card":
      return (
        <InfoCard
          title={block?.attributes?.title}
          text={block?.attributes?.text}
          url={block?.attributes?.url}
          icon={
            block.attributes.icon?.json
              ? JSON.parse(block.attributes?.icon?.json)
              : null
          }
          variant={block.attributes?.variant}
          alignContent={block.attributes?.alignContent}
          backgroundColorHex={block.attributes?.backgroundColorHex}
          textColorHex={block.attributes?.textColorHex}
          iconColorHex={block.attributes?.iconColorHex}
          letterSpacing={block.attributes?.customLetterSpacing}
          rowSpacing={block.attributes?.rowSpacing}
          linkColor={block.attributes?.linkColor}
        />
      );

    case "mev/info-divider":
      return (
        <InfoDivider image={block.attributes.image}>
          <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />
        </InfoDivider>
      );

    case "mev/instagramfeed":
      return <InstagramFeed />;

    case "mev/location": {
      return (
        <Location
          btnUrl={block.attributes.buttonURL}
          variant={block.attributes.variant}
          disableGutters={block.attributes.disableGutters}
          company={block.attributes.company}
          street={block.attributes.street}
          postal={block.attributes.postal}
          email={block.attributes.email}
          phone={block.attributes.phone}
          lat={Number(block.attributes.lat)}
          lng={Number(block.attributes.lng)}
          label={block.attributes.buttonLabel}
        >
          <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />
        </Location>
      );
    }
    case "mev/metric":
      return (
        <Metric
          icon={
            block.attributes.icon?.json
              ? JSON.parse(block.attributes.icon?.json)
              : null
          }
          number={block.attributes.number}
          unit={block.attributes.unit}
          text={block.attributes.text}
          letterSpacing={block.attributes?.customLetterSpacing}
          rowSpacing={block.attributes?.rowSpacing}
          linkColor={block.attributes?.linkColor}
        />
      );

    case "mev/handpicked-product-category":
      return <ProductCategoryBlock categories={block.attributes?.categories} />;

    case "mev/hero":
      return (
        <Hero
          media={block.attributes?.media}
          mobileMedia={block.attributes?.mobileMedia}
          variant={block.attributes?.variant}
          mediaItems={block.attributes?.mediaItems}
          mediaItemsMobile={block.attributes?.mediaItemsMobile}
          backgroundColorHex={block.attributes?.backgroundColorHex}
          textColorHex={block.attributes?.textColorHex}
          alignContent={block?.attributes?.alignContent}
          letterSpacing={block.attributes?.customLetterSpacing}
          rowSpacing={block.attributes?.rowSpacing}
          linkColor={block.attributes?.linkColor}
          imagePlace={block.attributes?.imagePlace}
        >
          <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />
        </Hero>
      );

    case "mev/section":
      return (
        <Section
          title={block.attributes.title}
          description={block.attributes.description}
          loadButtonLabel={block.attributes.loadButtonLabel}
          maxColumns={block.attributes.maxColumns}
          maxRows={block.attributes.maxRows}
        >
          {block.innerBlocks.map((block, i) => (
            <GutenbergBlock key={i} block={block} seo={seo} />
          ))}
        </Section>
      );

    case "mev/read-more":
      return (
        <ReadMore
          readMoreText={block.attributes?.readMoreText}
          readLessText={block.attributes?.readLessText}
          readMoreIcon={
            block.attributes.readMoreIcon?.json
              ? JSON.parse(block.attributes.readMoreIcon?.json)
              : null
          }
          readLessIcon={
            block.attributes.readLessIcon?.json
              ? JSON.parse(block.attributes.readLessIcon?.json)
              : null
          }
        >
          <GutenbergBlocks blocks={block.innerBlocks} />
        </ReadMore>
      );

    case "mev/section-title":
      return (
        <SectionTitle
          title={block.attributes.title}
          position={
            block.attributes.mevAlign === "center" ? "centered" : "left"
          }
          linkLabel={block.attributes.urlLabel}
          linkUrl={block.attributes.page?.uri || block.attributes.url}
          textColor={block.attributes.textColor}
        >
          <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />
        </SectionTitle>
      );

    case "mev/single-quote":
      return (
        <SingleQuote
          quote={block?.attributes?.quote}
          author={block?.attributes?.author}
          className={block?.attributes?.className}
        />
      );

    case "mev/superhero": {
      return (
        <SuperHero activateAutoplay={block?.attributes?.activateAutoplay}>
          {block.innerBlocks.map((innerBlock, i) => (
            <GutenbergBlock key={i} block={innerBlock} seo={seo} />
          ))}
        </SuperHero>
      );
    }

    case "mev/superhero-slide": {
      return (
        <SuperheroSlide
          alignContent={block?.attributes?.alignContent}
          media={block.attributes?.media}
          image={block.attributes?.image}
          imageURL={block.attributes?.imageURL}
          letterSpacing={block.attributes?.customLetterSpacing}
          rowSpacing={block.attributes?.rowSpacing}
          linkColor={block.attributes?.linkColor}
          className={block.attributes?.className}
        >
          <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />
        </SuperheroSlide>
      );
    }

    case "mev/video-card":
      return (
        <VideoCard
          title={block.attributes.title}
          date={block.attributes.date}
          description={block.attributes.description}
          videoUrl={block.attributes.videoURL}
          siteUrl={block.attributes.page?.uri || block.attributes.siteURL}
          urlLabel={block.attributes.urlLabel}
          image={block.attributes.image}
          videoSource={block.attributes.videoSource}
          youTubeID={block.attributes.youtubeId}
        />
      );

    case "mev/lightbox-gallery": {
      const photos = block.innerBlocks
        .filter((block) => block.name === "mev/lightbox-gallery-image")
        .map((block) => ({
          sourceUrl: block?.attributes?.image?.sourceUrl,
          gatsbyImageData:
            block?.attributes?.image?.localFile?.childImageSharp
              ?.gatsbyImageData,
        }));

      if (!photos.length) return null;

      return (
        <LightBoxGallery
          numberOfVisibleImages={block?.attributes?.visibleImages}
          images={photos}
        />
      );
    }

    case "mev/masonry-grid": {
      const photos = block.innerBlocks.reduce((photos, block) => {
        if (block.name === "mev/masonry-grid-image") {
          photos.push({ image: block.attributes.image });
        }
        return photos;
      }, []);
      return <MasonryImageGrid photos={photos} />;
    }

    case "mev/quotes": {
      const quotes = block.innerBlocks.reduce((quotes, block) => {
        if (block.name === "mev/quote") {
          quotes.push({
            title: block.attributes.title,
            quote: block.attributes.quote,
            author: block.attributes.author,
            letterSpacing: block.attributes?.customLetterSpacing,
            rowSpacing: block.attributes?.rowSpacing,
            linkColor: block.attributes?.linkColor,
          });
        }
        return quotes;
      }, []);
      return <Quotes title={block.attributes.title} quotes={quotes} />;
    }

    case "contact-form-7/contact-form-selector":
      return (
        <ContactForm
          action={process.env.GATSBY_WORDPRESS_URL + block.attributes.url}
          html={block.attributes.rendered}
          id={block.attributes.id}
        />
      );

    case "woocommerce/checkout":
      return <Checkout />;

    case "woocommerce/featured-product":
      return (
        <FeaturedProduct
          align={block.attributes?.align}
          className={block.attributes?.className}
          contentAlign={block.attributes?.contentAlign}
          overlayColor={block.attributes?.overlayColor}
          showDesc={block.attributes?.showDesc}
          showPrice={block.attributes?.showPrice}
          height={block.attributes?.height}
          product={block.attributes?.product}
          dimRatio={block.attributes?.dimRatio}
          media={block.attributes?.media || block.attributes?.product?.image}
        >
          <GutenbergBlocks blocks={block.innerBlocks} seo={seo} />
        </FeaturedProduct>
      );
    case "woocommerce/handpicked-products":
      return (
        <MultipleProductsBlock
          rows={block.attributes?.rows}
          columns={block.attributes?.columns}
          showAddToCartButton={block.attributes?.productAddToCartButton}
          showPrice={block.attributes?.productPrice}
          showTitle={block.attributes?.productTitle}
          products={block.attributes?.products}
          alignButtons={block.attributes?.alignButtons}
          orderby={block.attributes?.orderby}
        />
      );
    case "woocommerce/product-best-sellers":
      return (
        <MultipleProductsBlock
          rows={block.attributes?.rows}
          columns={block.attributes?.columns}
          showAddToCartButton={block.attributes?.productAddToCartButton}
          showPrice={block.attributes?.productPrice}
          showTitle={block.attributes?.productTitle}
          products={block.attributes?.products}
          alignButtons={block.attributes?.alignButtons}
        />
      );
    case "woocommerce/product-category":
      return (
        <MultipleProductsBlock
          rows={block.attributes?.rows}
          columns={block.attributes?.columns}
          showAddToCartButton={block.attributes?.productAddToCartButton}
          showPrice={block.attributes?.productPrice}
          showTitle={block.attributes?.productTitle}
          products={block.attributes?.products}
          alignButtons={block.attributes?.alignButtons}
          orderby={block.attributes?.orderby}
        />
      );
    case "woocommerce/product-new":
      return (
        <MultipleProductsBlock
          rows={block.attributes?.rows}
          columns={block.attributes?.columns}
          showAddToCartButton={block.attributes?.productAddToCartButton}
          showPrice={block.attributes?.productPrice}
          showTitle={block.attributes?.productTitle}
          products={block.attributes?.products}
          alignButtons={block.attributes?.alignButtons}
        />
      );
    case "woocommerce/product-on-sale":
      return (
        <MultipleProductsBlock
          rows={block.attributes?.rows}
          columns={block.attributes?.columns}
          showAddToCartButton={block.attributes?.productAddToCartButton}
          showPrice={block.attributes?.productPrice}
          showTitle={block.attributes?.productTitle}
          products={block.attributes?.products}
          alignButtons={block.attributes?.alignButtons}
          orderby={block.attributes?.orderby}
        />
      );
    case "yoast-seo/breadcrumbs":
      return <Breadcrumbs breadcrumbList={seo?.breadcrumbs} />;

    default:
      if (block.isDynamic) {
        return parse(block.dynamicContent);
      } else {
        return parse(block.saveContent);
      }
  }
}

GutenbergBlock.propTypes = {
  block: PropTypes.object.isRequired,
  seo: PropTypes.object.isRequired,
};

export const query = graphql`
  fragment GutenbergBlock on WpBlock {
    name
    isDynamic
    dynamicContent
    saveContent

    ... on WpMevCardBlock {
      attributes {
        cardImage: image {
          altText
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        imageWide: image {
          altText
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData(aspectRatio: 2, width: 1030)
            }
          }
        }
        page {
          uri
        }
        url
        title
        urlLabel
        description
        twoCol
        variation
        opacity
        backgroundColorHex
        buttonTextColorHex
        textColorHex
        customLetterSpacing: letterSpacing
        rowSpacing
        linkColor: customLinkColorHex
      }
    }

    ... on WpMevContainerBlock {
      attributes {
        disableGutters
        disableMargins
        maxWidth
        variant
        alignContent
        disableAnimation
        backgroundColorHex
        textColorHex
        className
        borderRadius
      }
    }
    ... on WpMevEmployeeCardContainerBlock {
      attributes {
        nrOfColumns: columns
        postLayout
        className
      }
    }
    ... on WpMevCardContainerBlock {
      attributes {
        autoplay
        nrOfColumns: columns
        postLayout
        className
      }
    }

    ... on WpMevEmployeeCardBlock {
      attributes {
        city
        className
        email
        name
        phone
        title
        image {
          altText
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        backgroundColorHex
        textColorHex
        customLetterSpacing: letterSpacing
        rowSpacing
        linkColor: customLinkColorHex
      }
    }
    ... on WpMevFaqContainerBlock {
      attributes {
        textColorHex
        backgroundColorHex
      }
    }
    ... on WpMevFaqBlock {
      attributes {
        answer
        question
        backgroundColorHex
        textColorHex
        linkColor: customLinkColorHex
      }
    }

    ... on WpMevHiddenBlock {
      attributes {
        showOnDesktop
        showOnTablet
        showOnPhone
      }
    }

    ... on WpMevHandpickedProductCategoryBlock {
      attributes {
        categories {
          name
          uri
          image {
            localFile {
              childImageSharp {
                gatsbyImageData
              }
            }
          }
        }
      }
    }

    ... on WpMevInfoCardBlock {
      attributes {
        title
        text
        url
        icon {
          json
        }
        backgroundColorHex
        textColorHex
        iconColorHex
        variant
        alignContent
        customLetterSpacing: letterSpacing
        rowSpacing
        linkColor: customLinkColorHex
      }
    }

    ... on WpMevInfoDividerBlock {
      attributes {
        image {
          altText
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
    }

    ... on WpMevLocationBlock {
      attributes {
        buttonLabel
        buttonURL
        company
        disableGutters
        lat
        lng
        postal
        street
        variant
        email
        phone
      }
    }

    ... on WpMevMetricBlock {
      attributes {
        icon {
          json
        }
        text
        unit
        number
        customLetterSpacing: letterSpacing
        rowSpacing
        linkColor: customLinkColorHex
      }
    }

    ... on WpMevHeroBlock {
      attributes {
        backgroundColorHex
        textColorHex
        variant
        media {
          mimeType
          altText
          focalPoint {
            objectPosition
          }
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH, formats: PNG)
            }
          }
        }
        mobileMedia {
          mimeType
          altText
          focalPoint {
            objectPosition
          }
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
        mediaItems {
          mimeType
          altText
          focalPoint {
            objectPosition
          }
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
        mediaItemsMobile {
          mimeType
          altText
          focalPoint {
            objectPosition
          }
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
        alignContent
        customLetterSpacing: letterSpacing
        rowSpacing
        linkColor: customLinkColorHex
        imagePlace
      }
    }

    ... on WpMevLightboxGalleryBlock {
      attributes {
        visibleImages
      }
    }

    ... on WpMevLightboxGalleryImageBlock {
      attributes {
        image {
          localFile {
            childImageSharp {
              gatsbyImageData(width: 800, quality: 80)
            }
          }
          sourceUrl
        }
      }
    }

    ... on WpMevMasonryGridImageBlock {
      attributes {
        image {
          altText
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
    }

    ... on WpMevReadMoreBlock {
      attributes {
        readMoreText
        readLessText
        readMoreIcon {
          json
        }
        readLessIcon {
          json
        }
      }
    }

    ... on WpMevSectionBlock {
      attributes {
        title
        description
        loadButtonLabel
        maxColumns
        maxRows
      }
    }

    ... on WpMevSectionTitleBlock {
      attributes {
        title
        mevAlign: align
        urlLabel
        url
        textColor
        page {
          uri
        }
      }
    }

    ... on WpMevSingleQuoteBlock {
      attributes {
        author
        className
        quote
      }
    }

    ... on WpMevSuperheroBlock {
      attributes {
        activateAutoplay
      }
    }

    ... on WpMevSuperheroSlideBlock {
      attributes {
        className
        media {
          altText
          mimeType
          mediaItemUrl
          focalPoint {
            objectPosition
          }
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH, formats: PNG)
            }
          }
        }
        image {
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        imageURL
        alignContent
        customLetterSpacing: letterSpacing
        rowSpacing
        linkColor: customLinkColorHex
      }
    }

    ... on WpMevVideoCardBlock {
      attributes {
        description
        image {
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        page {
          uri
        }
        siteURL
        title
        urlLabel
        videoURL
        videoSource
        youtubeId
      }
    }

    ... on WpMevQuotesBlock {
      attributes {
        title
      }
    }

    ... on WpMevQuoteBlock {
      attributes {
        title
        quote
        author
        customLetterSpacing: letterSpacing
        rowSpacing
        linkColor: customLinkColorHex
      }
    }

    ... on WpCoreButtonsBlock {
      attributes {
        ... on WpCoreButtonsBlockAttributes {
          contentJustification
        }
      }
    }

    ... on WpCoreButtonBlock {
      attributes {
        ... on WpCoreButtonBlockAttributes {
          text
          className
          url
          linkTarget
          rel
          className
          backgroundColorHex
          textColorHex
          borderRadius
          padding
          customLetterSpacing
          rowSpacing
        }
      }
    }

    ... on WpCoreCodeBlock {
      attributes {
        code: content
      }
    }

    ... on WpCoreColumnsBlock {
      attributes {
        ... on WpCoreColumnsBlockAttributes {
          className
          blockGap
          padding
          backgroundColorHex
          textColorHex
        }
      }
    }

    ... on WpCoreColumnBlock {
      attributes {
        ... on WpCoreColumnBlockAttributes {
          className
          verticalAlignment
          width
          backgroundColorHex
          textColorHex
          padding
          columnHeight
        }
      }
    }

    ... on WpCoreImageBlock {
      attributes {
        ... on WpCoreImageBlockAttributes {
          className
          imageWidth: width
          imageHeight: height
          image {
            altText
            focalPoint {
              objectPosition
            }
            localFile {
              childImageSharp {
                gatsbyImageData
              }
            }
          }
          alt
          title
          href
          rel
          caption
          sizeSlug
        }
      }
    }

    ... on WpCoreGalleryBlock {
      attributes {
        ... on WpCoreGalleryBlockAttributes {
          gridCol: columns
          fixedHeight
          imageCrop
          className
          images {
            image {
              altText
              focalPoint {
                objectPosition
              }
              localFile {
                childImageSharp {
                  gatsbyImageData(placeholder: DOMINANT_COLOR)
                }
              }
            }
          }
        }
      }
    }

    ... on WpCoreMediaTextBlock {
      attributes {
        ... on WpCoreMediaTextBlockAttributes {
          imageFill
          backgroundColor
          isStackedOnMobile
          mediaPosition
          mediaWidth
          mevAlign: align
          verticalAlignment
          className
          media {
            altText
            focalPoint {
              objectPosition
            }
            localFile {
              childImageSharp {
                gatsbyImageData
              }
            }
          }
          customLetterSpacing
          rowSpacing
          linkColor: linkColorHex
        }
      }
    }

    ... on WpContactForm7ContactFormSelectorBlock {
      attributes {
        id
        rendered
        url
      }
    }

    ... on WpCoreLatestPostsBlock {
      attributes {
        ... on WpCoreLatestPostsBlockAttributes {
          columns
          postLayout
          posts {
            date(formatString: "DD.MM.YYYY")
            excerpt
            title
            uri
            featuredImage {
              node {
                altText
                focalPoint {
                  objectPosition
                }
                localFile {
                  childImageSharp {
                    gatsbyImageData
                  }
                }
              }
            }
            author {
              node {
                name
              }
            }
          }
          displayFeaturedImage
          displayPostContent
          displayAuthor
          displayPostDate
          excerptLength
          rowSpacing
        }
      }
    }

    ... on WpCoreParagraphBlock {
      attributes {
        ... on WpCoreParagraphBlockAttributes {
          backgroundColorHex
          textColorHex
          content
          align
          anchor
          className
          parsedFontSize
          direction
          textTransform
          fontWeight
          fontStyle
          letterSpacing
          fontSizeMobile
          rowSpacing
          linkColor: linkColorHex
          inlineImageWidth
        }
      }
    }
    ... on WpCoreHeadingBlock {
      attributes {
        ... on WpCoreHeadingBlockAttributes {
          anchor
          content
          textAlign
          backgroundColorHex
          className
          parsedFontSize
          textColorHex
          level
          margin
          textTransform
          fontWeight
          fontStyle
          letterSpacing
          fontSizeMobile
          rowSpacing
          linkColor: linkColorHex
        }
      }
    }
    ... on WpCoreFileBlock {
      attributes {
        ... on WpCoreFileBlockAttributes {
          fileName
          href
          downloadButtonText
          showDownloadButton
        }
      }
    }
    ... on WpWoocommerceFeaturedProductBlock {
      attributes {
        align
        className
        contentAlign
        dimRatio
        height
        overlayColor
        showDesc
        showPrice
        media {
          mimeType
          altText
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
          focalPoint {
            objectPosition
          }
        }
        product {
          description
          name
          link
          image {
            mimeType
            altText
            localFile {
              childImageSharp {
                gatsbyImageData
              }
            }
          }
          ...ProductTypes
        }
      }
    }
    ... on WpWoocommerceProductBestSellersBlock {
      attributes {
        rows
        columns
        productAddToCartButton
        productPrice
        productTitle
        alignButtons
        products {
          ...ProductTypes
        }
      }
    }
    ... on WpWoocommerceProductCategoryBlock {
      attributes {
        rows
        columns
        productAddToCartButton
        productPrice
        productTitle
        alignButtons
        orderby
        products {
          ...ProductTypes
        }
      }
    }
    ... on WpWoocommerceHandpickedProductsBlock {
      attributes {
        columns
        productAddToCartButton
        productPrice
        productTitle
        alignButtons
        orderby
        products {
          ...ProductTypes
        }
      }
    }
    ... on WpWoocommerceProductNewBlock {
      attributes {
        rows
        columns
        productAddToCartButton
        productPrice
        productTitle
        alignButtons
        products {
          ...ProductTypes
        }
      }
    }
    ... on WpWoocommerceProductOnSaleBlock {
      attributes {
        rows
        columns
        productAddToCartButton
        productPrice
        productTitle
        alignButtons
        orderby
        products {
          ...ProductTypes
        }
      }
    }
    ... on WpCoreCoverBlock {
      attributes {
        ... on WpCoreCoverBlockAttributes {
          align
          overlayColor
          dimRatio
          duotoneColorValues
          media {
            mimeType
            altText
            focalPoint {
              objectPosition
            }
            localFile {
              publicURL
              childImageSharp {
                gatsbyImageData(layout: FULL_WIDTH)
              }
            }
          }
          minHeight
          minHeightUnit
          contentPosition
          hasParallax
          padding
        }
      }
    }

    ... on WpCoreSpacerBlock {
      attributes {
        ... on WpCoreSpacerBlockAttributes {
          spacerHeight: height
          spacerHeightMobile
          className
        }
      }
    }

    ... on WpYoastSeoBreadcrumbsBlock {
      dynamicContent
    }

    ... on WpCoreListBlock {
      attributes {
        ... on WpCoreListBlockAttributes {
          values
          letterSpacing
          rowSpacing
          linkColor: linkColorHex
        }
      }
    }
  }

  fragment ProductTypeFields on Node {
    ... on WpSimpleProduct {
      id
      name
      uri
      sku
      databaseId
      stockStatus
      onSale
      shortDescription
      date
      attributes {
        nodes {
          name
          options
          label
          variation
          visible
        }
      }
      price
      regularPrice
      salePrice
      image {
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
    ... on WpVariableProduct {
      id
      name
      description
      uri
      sku
      databaseId
      stockStatus
      price
      regularPrice
      salePrice
      onSale
      shortDescription
      date
      attributes {
        nodes {
          name
          label
          options
          variation
          visible
          ... on WpGlobalProductAttribute {
            terms {
              nodes {
                slug
                name
              }
            }
          }
        }
      }
      variations {
        nodes {
          id
          databaseId
          name
          description
          price
          uri
          sku
          onSale
          stockStatus
          salePrice
          attributes {
            nodes {
              name
              value
              label
              option
            }
          }
          image {
            localFile {
              childImageSharp {
                gatsbyImageData
              }
            }
          }
        }
      }
      image {
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
    ... on WpExternalProduct {
      id
      name
      uri
      databaseId
      onSale
      shortDescription
      date
      attributes {
        nodes {
          name
          options
        }
      }
      price
      regularPrice
      salePrice
      image {
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
    ... on WpGroupProduct {
      id
      name
      uri
      databaseId
      onSale
      shortDescription
      date
      attributes {
        nodes {
          name
          options
        }
      }
      price
      image {
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }

  fragment ProductTypes on Node {
    ...ProductTypeFields
  }

  fragment ReusableBlock on WpBlock {
    ... on WpCoreBlock {
      attributes {
        ref
        reusableBlock {
          blocks {
            ...GutenbergBlock
            innerBlocks {
              ...GutenbergBlock
              innerBlocks {
                ...GutenbergBlock
                innerBlocks {
                  ...GutenbergBlock
                  innerBlocks {
                    ...GutenbergBlock
                    innerBlocks {
                      ...GutenbergBlock
                      innerBlocks {
                        ...GutenbergBlock
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment GutenbergBlocks on WpBlock {
    ...GutenbergBlock
    ...ReusableBlock
    innerBlocks {
      ...GutenbergBlock
      ...ReusableBlock
      innerBlocks {
        ...GutenbergBlock
        ...ReusableBlock
        innerBlocks {
          ...GutenbergBlock
          ...ReusableBlock
          innerBlocks {
            ...GutenbergBlock
            ...ReusableBlock
            innerBlocks {
              ...GutenbergBlock
              ...ReusableBlock
              innerBlocks {
                ...GutenbergBlock
                ...ReusableBlock
                innerBlocks {
                  ...GutenbergBlock
                  ...ReusableBlock
                }
              }
            }
          }
        }
      }
    }
  }
`;
